import lodash from "lodash";
import { defineAsyncComponent } from "vue";
import {
    createFieldCheckbox,
    createFieldDatetime,
    createFieldRadioGroup,
    createFieldSelect,
    createFieldTextArea
} from "../../../../components/input/template-builder/template-builder.logic";
import { createColumnDatetime, createColumnInput, createColumnSelect } from "../../../../components/table/c-table.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import { conditionFormChecker, putField } from "../../../../utils/form";
import { has } from "../../../../utils/menu-context";
import { callDepartment } from "../../master-mgt/department-responsible/department-responsible.logic";
import { callInspector, createField as createFieldInspection } from "../company-request/company-inspection.logic";

const CardBorder = defineAsyncComponent(() => import("../../../../components/card/CardBorder"));
const TemplateBuilder = defineAsyncComponent(() => import("../../../../components/input/template-builder/TemplateBuilder"));
const CompanyProductList = defineAsyncComponent(() => import("../company-request/CompanyProductList"));
const CompanyCountryList = defineAsyncComponent(() => import("../company-request/CompanyCountryList"));

export const callInspectionType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "INSPECTION_TYPE", value: searchQuery });
export const callCompanyCalendarStatus = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "COMPANY_CALENDAR_STATUS", value: searchQuery });
export const callCompanyByThNoOrNameTh = ({ searchQuery, departmentId }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "refStatus.key", type: "String", operator: "=", value: "Approve" },
        //{ name: "refThNoStatus.key", type: "String", operator: "=", value: "A" },
        { name: "isTmp", type: "Boolean", operator: "=", value: false }
    ];
    if (departmentId) {
        where.push({ name: "refDepartment.id", type: "UUID", operator: "=", value: departmentId });
    }
    if (searchQuery) {
        const thNo = searchQuery?.split(" ")?.[0];
        const nameTh = searchQuery
            ?.split(" ")
            ?.slice?.(1)
            ?.join(" ");
        if (thNo && nameTh) {
            where.push({ openBucket: true, name: "thNo", type: "String", operator: "like", value: thNo });
            where.push({ closeBucket: true, name: "nameTh", type: "String", operator: "like", value: nameTh });
        } else {
            where.push({ openBucket: true, name: "thNo", type: "String", operator: "like", value: searchQuery?.trim() });
            where.push({ logic: "OR", closeBucket: true, name: "nameTh", type: "String", operator: "like", value: searchQuery?.trim() });
        }
    }
    return store
        .dispatch("resource/v1/transaction/company/search", { criteria: { select: ["*"], page: 0, size: 1000000, where, sort: [] } })
        .then(page => page.data);
};

export const createColumns = ({ props }) => {
    let defaultCompanyDepartment = null;
    if (!has({ menuContext: props?.menuContext, key: "forCompany" })) {
        if (!has({ menuContext: props?.menuContext, key: "canSearchByCompanyDepartment" })) {
            defaultCompanyDepartment =
                store.state.resource.auth.info?.user?.refDepartment?.refParent || store.state.resource.auth.info?.user?.refDepartment;
        }
    }
    const columns = [
        createColumnInput({ title: () => i18n.global.t("model.company.thNo"), dataIndex: "refCompany.thNo", align: "center" }),
        createColumnInput({ title: () => i18n.global.t("model.company.nameTh"), dataIndex: "refCompany.nameTh" }),
        createColumnSelect({
            title: () => i18n.global.t("model.company.refDepartment"),
            dataIndex: "refCompany.refDepartment.nameTh",
            customRender: ({ record }) => <div>{record.refCompany?.refDepartment?.nameTh}</div>,
            inputProp: () => ({ list: callDepartment, optLabel: "nameTh" }),
            search: {
                name: "refCompany.refDepartment.id",
                visible: () => has({ menuContext: props?.menuContext, key: "canSearchByCompanyDepartment" }),
                field: { searchValue: defaultCompanyDepartment }
            }
        }),
        createColumnDatetime({ title: () => i18n.global.t("model.companyCalendar.appointmentDatetime"), dataIndex: "appointmentDatetime" }),
        createColumnDatetime({
            title: () => i18n.global.t("model.companyCalendar.appointmentPostponeDatetime"),
            dataIndex: "appointmentPostponeDatetime"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.companyCalendar.refInspectionType"),
            dataIndex: "refInspectionType.value",
            customRender: ({ record }) => <div>{record.refInspectionType?.value}</div>,
            inputProp: () => ({ list: callInspectionType, optLabel: "value" }),
            align: "center",
            search: { name: "refInspectionType.id" }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.companyCalendar.refStatus"),
            dataIndex: "refStatus.value",
            customRender: ({ record }) => <div>{record.refStatus?.value}</div>,
            inputProp: () => ({ list: callCompanyCalendarStatus, optLabel: "value" }),
            align: "center",
            search: { name: "refStatus.id" }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.companyCalendar.refInspector"),
            dataIndex: "refInspector.fullName",
            customRender: ({ record }) => <div>{record.refInspector?.fullName}</div>,
            inputProp: () => ({ list: callInspector, optLabel: "fullName" }),
            search: { name: "refInspector.id" }
        })
    ];
    return columns;
};

export const createField = ({ props, data, key, isLoading }) => {
    const { viewOnly, menuContext } = props;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "Field" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    return (
                        <CardBorder title={i18n.global.t("views.company.companyCalendar.title")}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={createFieldCompanyCalendar({ props, data, key, isLoading })}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                        </CardBorder>
                    );
                }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyProduct" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const componentProp = conditionFormChecker({
                        props,
                        data,
                        executeKey: "componentProp",
                        params: { sectionKey: "CompanyProduct" }
                    });
                    return (
                        <CardBorder title={i18n.global.t("views.company.companyProduct.title")}>
                            <CompanyProductList
                                menuContext={menuContext}
                                viewOnly={viewOnly}
                                dataProp={data}
                                itemsKey={`${prefix}refCompany.companyProducts`}
                                visibleCompany={false}
                                {...componentProp}
                            ></CompanyProductList>
                        </CardBorder>
                    );
                }
            },
            {
                key: `${prefix}companyProducts`,
                label: () => i18n.global.t("model.companyProduct.modelName"),
                changeLog: { getValueChildren: ({ record }) => record.refProduct?.nameTh }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "CompanyCountry" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    const componentProp = conditionFormChecker({
                        props,
                        data,
                        executeKey: "componentProp",
                        params: { sectionKey: "CompanyCountry" }
                    });
                    return (
                        <CardBorder title={i18n.global.t("views.company.companyCountry.title")}>
                            <CompanyCountryList
                                menuContext={menuContext}
                                viewOnly={viewOnly}
                                dataProp={data}
                                itemsKey={`${prefix}refCompany.companyCountries`}
                                visibleCompany={false}
                                {...componentProp}
                            ></CompanyCountryList>
                        </CardBorder>
                    );
                }
            },
            {
                key: `${prefix}companyCountries`,
                label: () => i18n.global.t("model.companyCountry.modelName"),
                changeLog: { getValueChildren: ({ record }) => record.refCountry?.nameTh }
            },
            {
                visible: () => conditionFormChecker({ props, data, executeKey: "visibleSection", params: { sectionKey: "Inspection" } }),
                colProp: () => ({ xs: 24 }),
                formItemRender: () => {
                    return (
                        <CardBorder title={i18n.global.t("views.company.companyRequest.review.title")}>
                            <TemplateBuilder
                                rowProp={data.rowProp}
                                useForm={data.useForm}
                                fields={createFieldInspection({ props, data, key: "refCompanyInspection", isLoading })}
                                skeleton={isLoading.value}
                            ></TemplateBuilder>
                        </CardBorder>
                    );
                }
            }
        ]
    });
};

export const createFieldCompanyCalendar = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refCompany`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyCalendar.refCompany"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refCompany,
                            "onUpdate:value": value => (model.refCompany = value),
                            disabled: viewOnly,
                            list: ({ searchQuery }) =>
                                callCompanyByThNoOrNameTh({
                                    searchQuery,
                                    departmentId: !has({ menuContext: props?.menuContext, key: "canSearchByCompanyDepartment" })
                                        ? store.state.resource.auth.info?.user?.refDepartment?.refParent?.id || store.state.resource.auth.info?.user?.refDepartment?.id
                                        : null
                                }),
                            renderLabel: ({ item }) => (
                                <span>
                                    {item.thNo} {item.nameTh}
                                </span>
                            )
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: {
                    getValue: ({ record, field }) => {
                        if (lodash.get(record, field.key) === null) return null;
                        return lodash.get(record, `${field.key}.thNo`) && lodash.get(record, `${field.key}.nameTh`)
                            ? `${lodash.get(record, `${field.key}.thNo`)} ${lodash.get(record, `${field.key}.nameTh`)}`
                            : undefined;
                    }
                }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refInspector`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyCalendar.refInspector"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refInspector,
                            "onUpdate:value": value => (model.refInspector = value),
                            disabled: viewOnly,
                            list: callInspector,
                            optLabel: "fullName"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.fullName`) }
            }),
            createFieldRadioGroup({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refInspectionType`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyCalendar.refInspectionType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refInspectionType,
                            "onUpdate:value": value => (model.refInspectionType = value),
                            disabled: viewOnly,
                            list: callInspectionType,
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldCheckbox({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}isInvestFollowup`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyCalendar.isInvestFollowup"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            checked: model.isInvestFollowup,
                            "onUpdate:checked": checked => (model.isInvestFollowup = checked),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldDatetime({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}appointmentDatetime`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyCalendar.appointmentDatetime"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.appointmentDatetime,
                            "onUpdate:value": value => (model.appointmentDatetime = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}desc`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyCalendar.desc"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.desc,
                            "onUpdate:value": value => (model.desc = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldDatetime({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}appointmentPostponeDatetime`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyCalendar.appointmentPostponeDatetime"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.appointmentPostponeDatetime,
                            "onUpdate:value": value => (model.appointmentPostponeDatetime = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                }
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}companyRemark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyCalendar.companyRemark"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.companyRemark,
                            "onUpdate:value": value => (model.companyRemark = value),
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldRadioGroup({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refStatus`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
                label: () => i18n.global.t("model.companyCalendar.refStatus"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refStatus,
                            "onUpdate:value": value => (model.refStatus = value),
                            disabled: viewOnly,
                            list: ({ searchQuery }) =>
                                callCompanyCalendarStatus({ searchQuery }).then(statuses =>
                                    statuses.filter(d => ["Postpone", "Approve"].includes(d.key))
                                ),
                            optLabel: "attribute.actionText"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            })
        ]
    });
};
